import {
  Box,
  TextField,
  FormControlLabel,
  Grid,
  MenuItem,
  RadioGroup,
  Radio,
  Button,
  IconButton,
  FormHelperText,
  Checkbox,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import { FieldArray, Field } from "formik";
import {
  REPORT_NOTE_SECTIONS,
  OCCURRENCE_OPTIONS,
  SUBSECTION_OPTIONS,
  METRICS,
  OPERATORS,
  STATUS_VALUES,
  NOTE_SECTION_VALUES,
  NOTE_SUB_SECTION_VALUES,
  METRICS_BY_NOTE_SECTION,
  isUnitOptionalByMetric,
} from "static/alert_protocol";
import { DeleteOutline } from "@mui/icons-material";
import DeviceTypeFilter from "components/common/filters/device_type/DeviceTypeFilter";
import AccountFilter from "components/common/filters/account/AccountFilter";
import { makeStyles } from "@mui/styles";
import { isValidArray } from "utils/array";

const useStyles = makeStyles(() => ({
  label: {
    fontSize: "16px",
    lineHeight: "20px",
    fontWeight: 400,
    margin: 0,
  },
}));

const AlertProtocolForm = ({
  values,
  errors,
  touched,
  setFieldValue,
  handleDeleteMetric,
  isCopyMode,
  isEditMode,
  selectedContent,
  getUnitOptions,
}) => {
  const classes = useStyles();
  const { alertProtocols } = useSelector((state) => state.alertProtocol);

  const isArrhythmiaAF =
    values.noteSection === NOTE_SECTION_VALUES.ARRHYTHMIA &&
    values.noteSubSection === NOTE_SUB_SECTION_VALUES.AF;
  const isAfBurden = values.noteSection === NOTE_SECTION_VALUES.AF_BURDEN;

  const getAvailableMetrics = (currentIndex) => {
    const selectedMetrics = values.conditions
      .map((m, idx) => (idx !== currentIndex && m.metric ? m.metric : null))
      .filter(Boolean);

    const allowedMetrics = METRICS_BY_NOTE_SECTION[values.noteSection] ?? [];

    return METRICS.filter(
      (m) =>
        allowedMetrics.includes(m.value) && !selectedMetrics.includes(m.value)
    );
  };

  const hasAvailableMetrics = () => {
    const selectedMetrics = values.conditions
      .map((m) => m.metric)
      .filter(Boolean);

    const allowedMetrics = METRICS_BY_NOTE_SECTION[values.noteSection] ?? [];

    return selectedMetrics.length < allowedMetrics.length;
  };

  const getAvailableOperators = (metric) => {
    const isBatteryStatus =
      values.noteSection === NOTE_SECTION_VALUES.BATTERY && metric === "status";

    return isBatteryStatus
      ? OPERATORS.filter((operator) => operator.value === "eq")
      : OPERATORS;
  };

  const handleMetricChange = (index, metric) => {
    setFieldValue(`conditions.${index}.metric`, metric);

    const availableOperators = getAvailableOperators(metric);
    if (availableOperators.length === 1) {
      setFieldValue(
        `conditions.${index}.operator`,
        availableOperators[0].value
      );
    }

    if (isUnitOptionalByMetric(metric, values.noteSection)) {
      setFieldValue(`conditions.${index}.unit`, "");
    }
  };

  const isStatusDisabled = (status) => {
    const isInAlertProtocols = alertProtocols.some((protocol) => {
      if (protocol?._id !== values.noteSection) {
        return false;
      }

      return protocol.content
        ?.filter((contentItem) => contentItem?._id !== selectedContent?._id)
        .some((contentItem) =>
          contentItem.conditions?.some((c) => {
            return (
              c.metric === "status" &&
              isValidArray(c.value) &&
              c.value.includes(status)
            );
          })
        );
    });

    return isInAlertProtocols;
  };

  return (
    <Box p={0} className="alert-protocol">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <h6 className={classes.label}>Account and Device Information</h6>
        </Grid>

        <Grid item xs={12}>
          <AccountFilter
            displayAll={false}
            value={values.account}
            onChange={(e) => setFieldValue("account", e.target.value)}
            disabled={!isCopyMode}
          />
        </Grid>

        <Grid item xs={12}>
          <DeviceTypeFilter
            displayAll={false}
            value={values.deviceType}
            onChange={(e) => setFieldValue("deviceType", e.target.value)}
            disabled={isEditMode}
            multiple={true}
            required={true}
            error={touched.deviceType && Boolean(errors.deviceType)}
            helperText={touched.deviceType && errors.deviceType}
          />
        </Grid>

        <Grid item xs={12}>
          <h6 className={classes.label}>Notes Section</h6>
        </Grid>

        <Grid item xs={6}>
          <TextField
            label="Report Note Section"
            select
            fullWidth
            value={values.noteSection}
            onChange={(e) => setFieldValue("noteSection", e.target.value)}
            error={touched.noteSection && Boolean(errors.noteSection)}
            helperText={touched.noteSection && errors.noteSection}
          >
            {REPORT_NOTE_SECTIONS.map((section) => (
              <MenuItem key={section.value} value={section.value}>
                {section.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6}>
          <TextField
            label="Select Occurrence"
            select
            fullWidth
            value={values.occurrence}
            onChange={(e) => setFieldValue("occurrence", e.target.value)}
            error={touched.occurrence && Boolean(errors.occurrence)}
            helperText={touched.occurrence && errors.occurrence}
          >
            {OCCURRENCE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {values.noteSection === NOTE_SECTION_VALUES.ARRHYTHMIA && (
          <Grid item container alignItems="flex-start" columnSpacing={2}>
            <Grid item xs={6} container direction="column" spacing={1}>
              <Grid item>
                <h6 className={classes.label}>Note section types</h6>
              </Grid>
              <Grid item>
                <TextField
                  fullWidth
                  select
                  label="Subsections"
                  value={values.noteSubSection}
                  onChange={(e) =>
                    setFieldValue("noteSubSection", e.target.value)
                  }
                  error={
                    touched.noteSubSection && Boolean(errors.noteSubSection)
                  }
                  helperText={touched.noteSubSection && errors.noteSubSection}
                >
                  {SUBSECTION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>
        )}

        <Grid item container spacing={1}>
          {isArrhythmiaAF && (
            <Grid item xs={6} container spacing={1} direction="column">
              <Grid item className="mb-2">
                <h6 className={classes.label}>OAC</h6>
              </Grid>
              <Grid item>
                <RadioGroup
                  row
                  value={values?.oacStatus}
                  onChange={(e) => setFieldValue("oacStatus", e.target.value)}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" />}
                    label="No"
                  />
                  <FormControlLabel
                    value="unknown"
                    control={<Radio size="small" />}
                    label="Unknown"
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          )}

          {(isAfBurden || isArrhythmiaAF) && (
            <Grid item xs={6} container spacing={1} direction="column">
              <Grid item className="mb-2">
                <h6 className={classes.label}>LAAO</h6>
              </Grid>
              <Grid item>
                <RadioGroup
                  row
                  value={values?.laaoStatus}
                  onChange={(e) =>
                    setFieldValue("laaoStatus", e.target.value === "true")
                  }
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio size="small" />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio size="small" />}
                    label="No"
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          <h6 className={classes.label}>Metrics</h6>
        </Grid>

        <FieldArray name="conditions">
          {(fieldArrayHelpers) => (
            <>
              {values.conditions.map((condition, index) => (
                <Grid item xs={12} key={index}>
                  <Grid container columnSpacing={1} alignItems="flex-start">
                    <Grid
                      item
                      xs={11}
                      container
                      alignItems="flex-start"
                      columnSpacing={1}
                    >
                      <Grid item xs={3}>
                        <Field
                          as={TextField}
                          name={`conditions.${index}.metric`}
                          label="Metric Type"
                          select
                          fullWidth
                          onChange={(event) =>
                            handleMetricChange(index, event.target.value)
                          }
                          error={
                            errors.conditions?.[index]?.metric &&
                            touched.conditions?.[index]?.metric
                          }
                          helperText={
                            errors.conditions?.[index]?.metric &&
                            touched.conditions?.[index]?.metric
                              ? errors.conditions[index].metric
                              : ""
                          }
                        >
                          {getAvailableMetrics(index).map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>

                      <Grid item xs={3}>
                        <Field
                          as={TextField}
                          name={`conditions.${index}.operator`}
                          label="Operator"
                          select
                          fullWidth
                          error={
                            errors.conditions?.[index]?.operator &&
                            touched.conditions?.[index]?.operator
                          }
                          helperText={
                            errors.conditions?.[index]?.operator &&
                            touched.conditions?.[index]?.operator
                              ? errors.conditions[index].operator
                              : ""
                          }
                        >
                          {getAvailableOperators(condition.metric).map((op) => (
                            <MenuItem key={op.value} value={op.value}>
                              {op.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>

                      <Grid item xs={3}>
                        {condition.metric === "status" ? (
                          <Field
                            as={TextField}
                            name={`conditions.${index}.value`}
                            label="Value"
                            select
                            multiple
                            fullWidth
                            error={
                              errors.conditions?.[index]?.value &&
                              touched.conditions?.[index]?.value
                            }
                            helperText={
                              errors.conditions?.[index]?.value &&
                              touched.conditions?.[index]?.value
                                ? errors.conditions[index].value
                                : ""
                            }
                            sx={{
                              "& .MuiSelect-select": {
                                padding:
                                  values.conditions[index]?.value?.length > 0
                                    ? "11px 16px"
                                    : "16px",
                              },
                            }}
                            SelectProps={{
                              multiple: true,
                              renderValue: (selected) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                    alignItems: "center",
                                  }}
                                >
                                  {selected.length > 0 && (
                                    <>
                                      <Chip
                                        variant="outlined"
                                        label={selected[0]}
                                      />
                                      {selected.length > 1 && (
                                        <span>+{selected.length - 1}</span>
                                      )}
                                    </>
                                  )}
                                </Box>
                              ),
                            }}
                          >
                            {STATUS_VALUES.map((status) => (
                              <MenuItem key={status} value={status}>
                                <Checkbox
                                  checked={
                                    values.conditions[index]?.value?.includes(
                                      status
                                    ) || false
                                  }
                                />

                                {status}
                              </MenuItem>
                            ))}
                          </Field>
                        ) : (
                          <Field
                            as={TextField}
                            name={`conditions.${index}.value`}
                            label="Value"
                            type="number"
                            fullWidth
                            inputProps={{ max: 999 }}
                            value={values.conditions[index]?.value?.[0] || ""}
                            onChange={(event) => {
                              const value = event.target.value;

                              if (value?.trim() === "") {
                                setFieldValue(`conditions.${index}.value`, []);
                                return;
                              }

                              const num = Number(value);

                              if (
                                !Number.isNaN(num) &&
                                num >= 0 &&
                                num <= 999
                              ) {
                                setFieldValue(`conditions.${index}.value`, [
                                  String(num),
                                ]);
                              }
                            }}
                            error={
                              errors.conditions?.[index]?.value &&
                              touched.conditions?.[index]?.value
                            }
                            helperText={
                              errors.conditions?.[index]?.value &&
                              touched.conditions?.[index]?.value
                                ? errors.conditions[index].value
                                : ""
                            }
                          />
                        )}
                      </Grid>

                      <Grid item xs={3}>
                        <Field
                          as={TextField}
                          name={`conditions.${index}.unit`}
                          label="Unit"
                          select
                          fullWidth
                          disabled={isUnitOptionalByMetric(
                            condition.metric,
                            values.noteSection
                          )}
                          error={
                            errors.conditions?.[index]?.unit &&
                            touched.conditions?.[index]?.unit
                          }
                          helperText={
                            errors.conditions?.[index]?.unit &&
                            touched.conditions?.[index]?.unit
                              ? errors.conditions[index].unit
                              : ""
                          }
                        >
                          {getUnitOptions(index).map((unit) => (
                            <MenuItem key={unit.value} value={unit.value}>
                              {unit.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                    </Grid>

                    <Grid item xs={1}>
                      <IconButton
                        color="inherit"
                        size="medium"
                        className="mt-2"
                        onClick={() =>
                          handleDeleteMetric(
                            index,
                            fieldArrayHelpers,
                            values?.conditions
                          )
                        }
                      >
                        <DeleteOutline fontSize="medium" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              ))}

              <Grid item xs={12}>
                {hasAvailableMetrics() && (
                  <Button
                    variant="text"
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      fieldArrayHelpers.push({
                        metric: "",
                        operator: "",
                        value: [],
                        unit: "",
                      })
                    }
                    size="small"
                  >
                    Add Metrics
                  </Button>
                )}
              </Grid>

              {errors &&
                !isValidArray(errors?.conditions) &&
                values?.conditions?.length === 0 && (
                  <FormHelperText
                    error
                    className="mt-2"
                    style={{ marginLeft: "16px" }}
                  >
                    {errors?.conditions}
                  </FormHelperText>
                )}
            </>
          )}
        </FieldArray>

        <Grid item xs={12}>
          <h6 className={classes.label}>Alert Type</h6>
          <RadioGroup
            row
            value={values.alertType}
            onChange={(e) => setFieldValue("alertType", e.target.value)}
          >
            <FormControlLabel
              value="Warning"
              control={<Radio />}
              label="Warning"
            />
            <FormControlLabel
              value="Critical"
              control={<Radio />}
              label="Critical"
            />
          </RadioGroup>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AlertProtocolForm;
